// "The customer said yes" — the one thing that must never be missed.
//
// ── WHAT THIS FIXES ──────────────────────────────────────────────────────────
// A customer accepted a $33,950 quote through the public accept link. The app
// recorded it perfectly — audit row, line stage, an outbox event that delivered
// a note to the customer's GHL contact timeline — and told nobody. A note filed
// under the person who did the thing is not a notification; it is filed exactly
// where you do not look when you do not yet know the thing happened. The only
// in-app surface was the subtitle of a win-rate tile, and the RFQ list sorts by
// RECEIVED date, so an acceptance moved nothing on screen.
//
// Production had six such acceptances parked at line stage 'accepted', the
// oldest from 2026-07-13, the largest $149,995.
//
// ── WHY IT RENDERS NOTHING WHEN EMPTY ────────────────────────────────────────
// Deliberate, and the most important decision in this file. An attention
// surface that is always present becomes furniture within a week and stops
// being read. This occupies zero pixels on a quiet desk, so when it does appear
// its presence alone is the signal.

import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'wouter';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import {
  api,
  type AcceptedAwaitingActionResponse,
  type AcceptedAwaitingAction,
  type AcknowledgeAcceptanceResponse,
} from '@/lib/api';
import { timeAgo } from '@/lib/format';

/** Local, like the eight other copies in this folder. Consolidating them is
 *  worthwhile and is not this change. */
function fmtMoney(cents: number, currency: string): string {
  if (currency === 'JPY') return `${currency} ${Math.round(cents / 100).toLocaleString('en-US')}`;
  return `${currency} ${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export const ACCEPTED_AWAITING_ACTION_KEY = ['accepted-awaiting-action'];

/** Shared by the dashboard band and the RFQ-list rail so the two can never
 *  disagree about what is outstanding. */
export function useAcceptedAwaitingAction() {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ACCEPTED_AWAITING_ACTION_KEY,
    queryFn: () =>
      api<AcceptedAwaitingActionResponse>('/api/rfqs/accepted-awaiting-action', {
        tokenGetter: () => getToken(),
      }),
    // Short: this is the surface whose whole job is to be current. An
    // acceptance arriving while the tab sits open should appear on the next
    // focus, not after a manual reload.
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

export function useAcknowledgeAcceptance() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (quoteId: string) =>
      api<AcknowledgeAcceptanceResponse>(
        `/api/customer-quotes/${quoteId}/acknowledge-acceptance`,
        { method: 'POST', tokenGetter: () => getToken(), body: JSON.stringify({}) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ACCEPTED_AWAITING_ACTION_KEY });
      // The RFQ detail renders the Acknowledge control from the same field, so
      // the button must disappear without a reload. Keyed per-RFQ in
      // rfq-detail.tsx, so invalidate the whole family rather than guessing an id.
      qc.invalidateQueries({ queryKey: ['client-quotes'] });
    },
  });
}

/**
 * `band` — the roomy dashboard treatment, one row per acceptance.
 * `rail`  — the narrow strip pinned above the RFQ list, which is a 1-column
 *           sidebar, so it drops the contact and the Acknowledge button and
 *           leans on the link instead.
 */
export function AcceptedAwaitingActionPanel({ variant }: { variant: 'band' | 'rail' }) {
  const { data, isLoading, isError, error } = useAcceptedAwaitingAction();
  const ack = useAcknowledgeAcceptance();

  /*
   * A FAILED FETCH MUST NOT LOOK LIKE A QUIET DESK.
   *
   * Returning null on error was the first version of this, and it is the worst
   * possible behaviour for this particular panel: the whole point is that a
   * missed acceptance is invisible, and "render nothing" is indistinguishable
   * from "nothing is outstanding". A 500 — an unapplied migration, an expired
   * session, a bad deploy — would read as good news.
   *
   * So an error is LOUD and an empty list is silent, which is the opposite of
   * the usual convention and is deliberate.
   */
  if (isError) {
    return (
      <div
        className={variant === 'rail'
          ? 'px-4 py-2 border-b-2 border-amber-500/60 bg-amber-50 text-xs text-amber-900'
          : 'card mb-6 px-5 py-3 border-amber-500/50 text-xs text-amber-900 bg-amber-50'}
        role="alert"
      >
        <span className="font-semibold">Could not check for accepted quotes.</span>{' '}
        {(error as { message?: string })?.message ?? 'Unknown error'} — this panel is NOT saying
        there are none. Reload, and if it persists treat the RFQ list filtered by stage
        “Accepted” as the source of truth.
      </div>
    );
  }

  // No skeleton, no empty state. See the header.
  if (isLoading || !data || data.acceptances.length === 0) return null;

  const items = data.acceptances;
  const totalByCurrency = items.reduce<Record<string, number>>((acc, a) => {
    acc[a.currency] = (acc[a.currency] ?? 0) + a.customerTotalCents;
    return acc;
  }, {});

  if (variant === 'rail') {
    return (
      <div className="border-b-2 border-emerald-500/60 bg-emerald-50">
        <div className="px-4 py-2 flex items-center gap-2 text-xs font-semibold text-emerald-900">
          <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
          {items.length} accepted · awaiting action
        </div>
        <ul>
          {items.map((a) => (
            <li key={a.quoteId}>
              <Link
                href={`/rfqs/${a.rfqId}`}
                className="block px-4 py-2 border-t border-emerald-200/60 hover:bg-emerald-100/60 transition-colors"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs font-semibold truncate">
                    {a.customerLegalName ?? 'Unknown customer'}
                  </span>
                  {a.isAog && <span className="pill pill-danger font-semibold shrink-0">AOG</span>}
                </div>
                <div className="mt-0.5 flex items-baseline justify-between gap-2 text-xs">
                  <span className="font-mono text-muted-foreground truncate">{a.partNumber}</span>
                  <span className="font-semibold tabular-nums shrink-0">
                    {fmtMoney(a.customerTotalCents, a.currency)}
                  </span>
                </div>
                <div className="text-[11px] text-emerald-800">
                  accepted {a.acceptedAt ? timeAgo(a.acceptedAt) : 'at an unknown time'}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <section className="card overflow-hidden mb-6 border-emerald-500/50 ring-1 ring-emerald-500/20">
      <header className="px-5 py-3 border-b border-border flex items-center justify-between gap-3 flex-wrap bg-emerald-50">
        <h2 className="font-semibold text-sm flex items-center gap-2 text-emerald-900">
          <CheckCircle2 className="w-4 h-4" />
          {items.length} accepted {items.length === 1 ? 'quote' : 'quotes'} awaiting action
        </h2>
        <div className="text-xs text-emerald-900 tabular-nums font-medium">
          {Object.entries(totalByCurrency)
            .map(([cur, cents]) => fmtMoney(cents, cur))
            .join(' + ')}
        </div>
      </header>
      {ack.isError && (
        <div className="px-5 py-2 text-xs text-danger border-b border-border" role="alert">
          Could not acknowledge:{' '}
          {(ack.error as { message?: string })?.message ?? 'Unknown error'}. The acceptance is
          unchanged and still needs action.
        </div>
      )}
      <ul className="divide-y divide-border">
        {items.map((a) => (
          <AcceptanceRow
            key={a.quoteId}
            a={a}
            onAcknowledge={() => ack.mutate(a.quoteId)}
            acknowledging={ack.isPending && ack.variables === a.quoteId}
          />
        ))}
      </ul>
      {data.truncated && (
        <div className="px-5 py-2 text-xs text-muted-foreground border-t border-border">
          Showing the 50 oldest. Filter the RFQ list by stage “Accepted” for the rest.
        </div>
      )}
    </section>
  );
}

function AcceptanceRow({
  a, onAcknowledge, acknowledging,
}: {
  a: AcceptedAwaitingAction;
  onAcknowledge: () => void;
  acknowledging: boolean;
}) {
  return (
    <li className="px-5 py-3 flex items-start justify-between gap-4 flex-wrap">
      <div className="min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-semibold text-sm truncate">
            {a.customerLegalName ?? 'Unknown customer'}
          </span>
          {a.isAog && <span className="pill pill-danger font-semibold">AOG</span>}
          <span className="font-mono text-xs text-muted-foreground">{a.quoteRef}</span>
        </div>
        <div className="mt-1 text-xs text-muted-foreground">
          <span className="font-mono">{a.partNumber}</span>
          {' × '}{a.qty}
          {' · '}
          <span className="font-semibold text-foreground tabular-nums">
            {fmtMoney(a.customerTotalCents, a.currency)}
          </span>
          {a.isExchange && a.coreChargeCents != null && (
            <> {' · '}core {fmtMoney(a.coreChargeCents, a.currency)}</>
          )}
          {' · accepted '}
          {a.acceptedAt ? timeAgo(a.acceptedAt) : 'at an unknown time'}
        </div>
        {a.contactEmail && (
          <div className="mt-0.5 text-xs text-muted-foreground truncate">
            {a.contactFullName ? `${a.contactFullName} · ` : ''}{a.contactEmail}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={onAcknowledge}
          disabled={acknowledging}
          className="btn-outline text-xs"
          title="I have seen this. Removes it from this list without changing the order."
        >
          {acknowledging ? 'Acknowledging…' : 'Acknowledge'}
        </button>
        <Link href={`/rfqs/${a.rfqId}`} className="btn-primary text-xs">
          Open RFQ <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </li>
  );
}
